import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { clientAdminService } from "@/features/clients/services/client-admin.service";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { useResolvedById } from "@/shared/hooks/useResolvedById";
import { getApiErrorMessage } from "@/shared/lib/api-error";

const INPUT_CLASSES =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

interface ClientSelectFieldProps {
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
  inputId?: string;
}

export function ClientSelectField({
  value,
  onChange,
  disabled,
  inputId,
}: ClientSelectFieldProps) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ClientResponseDTO[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchClientById = useCallback(
    (id: number, signal: AbortSignal) =>
      clientAdminService.getById(id, { signal }),
    [],
  );
  const [selected, preloadClient, resolveError] = useResolvedById(
    value,
    fetchClientById,
  );

  useEffect(() => {
    if (resolveError) {
      toast.error(getApiErrorMessage(resolveError, "Erro ao carregar cliente"));
    }
  }, [resolveError]);

  const fetchClients = useCallback(
    async (term: string, signal: AbortSignal) => {
      const res = await clientAdminService.search(
        {
          name: term || undefined,
          size: 8,
          sortBy: "name",
          sortDirection: "ASC",
        },
        { signal },
      );
      return res.content;
    },
    [],
  );

  useDebouncedSearch({
    term: query,
    enabled: open,
    fetcher: fetchClients,
    setItems: setOptions,
    setLoading: setSearching,
    isValid: () => true,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar clientes")),
  });

  // Fecha ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = useCallback(
    (client: ClientResponseDTO) => {
      preloadClient(client.id, client);
      onChange(client.id);
      setOpen(false);
      setQuery("");
    },
    [onChange, preloadClient],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setQuery("");
  }, [onChange]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 border border-input rounded-md px-3 h-10 bg-background">
        <span className="text-sm flex-1 truncate">
          {selected.name}
          {selected.document && (
            <span className="text-muted-foreground ml-2 text-xs">
              {selected.document}
            </span>
          )}
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="text-muted-foreground hover:text-foreground shrink-0"
            aria-label="Remover cliente"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        id={inputId}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Buscar cliente pelo nome…"
        disabled={disabled}
        className={INPUT_CLASSES}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-52 overflow-y-auto">
          {searching ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Buscando...
            </div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {query.length === 0
                ? "Digite para buscar clientes"
                : "Nenhum cliente encontrado"}
            </div>
          ) : (
            options.map((client) => (
              <button
                key={client.id}
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex flex-col gap-0.5"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(client);
                }}
              >
                <span className="font-medium">{client.name}</span>
                {client.document && (
                  <span className="text-xs text-muted-foreground">
                    {client.document}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
